import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/auth";

const DOMAINS = [
  "legal", "housing", "immigration", "retail", "nourishment",
  "wellness", "trainers", "coaches", "therapists", "aesthetics",
];

function toggle(list, v) {
  const s = new Set(list || []);
  s.has(v) ? s.delete(v) : s.add(v);
  return Array.from(s);
}

export default function PersonDetail() {
  const { kind, personId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    setErr("");
    api.get(`/ops/people/${kind}/${personId}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatApiErrorDetail(e?.response?.data?.detail)));
  }, [kind, personId]);
  useEffect(() => { load(); }, [load]);

  if (err) return <div className="text-sm text-red-700" data-testid="ops-person-error">{err}</div>;
  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="ops-person-detail">
      <Link to="/ops/people" className="text-sm text-slate-500">← People</Link>
      {kind === "volunteer" && <VolunteerBody data={data} reload={load} />}
      {kind === "buddy" && <BuddyBody data={data} />}
      {kind === "staff" && <StaffBody data={data} />}
    </div>
  );
}

function VolunteerBody({ data, reload }) {
  const v = data.profile;
  const [busy, setBusy] = useState(false);
  const [domains, setDomains] = useState(v.domain_keys || []);
  const [phone, setPhone] = useState(v.phone || "");
  const [location, setLocation] = useState(v.location || "");
  const [notes, setNotes] = useState(v.operator_notes || "");

  const saveEdits = async () => {
    setBusy(true);
    try {
      await api.patch(`/ops/volunteers/${v.id}`, {
        phone, location, domain_keys: domains, notes,
      });
      toast.success("Volunteer profile updated.");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Update failed.");
    } finally { setBusy(false); }
  };
  const setStatus = async (status) => {
    setBusy(true);
    try {
      await api.post(`/ops/volunteers/${v.id}/status`, { status });
      toast.success(`Status set to ${status}.`);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Status change failed.");
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="bg-white rounded-xl border p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs text-slate-500 uppercase">Volunteer</div>
            <h1 className="text-2xl font-semibold text-slate-900" data-testid="ops-person-title">{v.name}</h1>
            <div className="text-sm text-slate-500">{v.email}</div>
          </div>
          <span className="text-lg font-semibold" data-testid="ops-person-status">{v.status || "active"}</span>
        </div>
      </div>
      <div className="bg-white rounded-xl border p-6 space-y-4">
        <h2 className="text-sm font-medium text-slate-600">Operational profile</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Phone"><input value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full text-sm border rounded px-3 py-2" data-testid="ops-vol-phone" /></Field>
          <Field label="Location / service area"><input value={location} onChange={(e) => setLocation(e.target.value)} className="w-full text-sm border rounded px-3 py-2" data-testid="ops-vol-location" /></Field>
        </div>
        <div>
          <div className="text-xs text-slate-500 uppercase mb-2">Domain associations</div>
          <div className="flex flex-wrap gap-2">
            {DOMAINS.map((d) => (
              <button key={d} type="button" onClick={() => setDomains(toggle(domains, d))}
                data-testid={`ops-vol-domain-${d}`}
                className={`px-3 py-1 rounded-full border text-xs font-semibold ${domains.includes(d) ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600"}`}>{d}</button>
            ))}
          </div>
        </div>
        <Field label="Operator notes (internal only)"><textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="w-full text-sm border rounded px-3 py-2" data-testid="ops-vol-notes" /></Field>
        <div className="flex flex-wrap gap-2 pt-2">
          <button type="button" disabled={busy} onClick={saveEdits} className="btc-btn-primary text-sm" data-testid="ops-vol-save">Save profile</button>
          <button type="button" disabled={busy || v.status === "active"} onClick={() => setStatus("active")} data-testid="ops-vol-activate" className="text-sm px-4 py-2 rounded bg-emerald-600 text-white font-semibold disabled:opacity-40">Activate</button>
          <button type="button" disabled={busy || v.status === "suspended"} onClick={() => setStatus("suspended")} data-testid="ops-vol-suspend" className="text-sm px-4 py-2 rounded bg-amber-600 text-white font-semibold disabled:opacity-40">Suspend</button>
          <button type="button" disabled={busy || v.status === "inactive"} onClick={() => setStatus("inactive")} data-testid="ops-vol-deactivate" className="text-sm px-4 py-2 rounded bg-slate-700 text-white font-semibold disabled:opacity-40">Deactivate</button>
        </div>
      </div>
      {v.interests && v.interests.length > 0 && (
        <div className="bg-white rounded-xl border p-6">
          <h2 className="text-sm font-medium text-slate-600 mb-2">From their application</h2>
          <div className="text-xs text-slate-500">Interests: {v.interests.join(", ") || "—"}</div>
          <div className="text-xs text-slate-500">Availability: {(v.availability || []).join(", ") || "—"}</div>
        </div>
      )}
    </>
  );
}

function BuddyBody({ data }) {
  const b = data.buddy;
  const u = data.user || {};
  return (
    <>
      <div className="bg-white rounded-xl border p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs text-slate-500 uppercase">Buddy (federated)</div>
            <h1 className="text-2xl font-semibold text-slate-900" data-testid="ops-person-title">{u.name || "—"}</h1>
            <div className="text-sm text-slate-500">{u.email}</div>
          </div>
          <span className="text-lg font-semibold" data-testid="ops-person-status">{b.status || "unknown"}</span>
        </div>
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3 mt-4">{data.federated_note}</p>
        <div className="mt-3">
          <Link to="/portal/admin/buddies" className="btc-btn-primary text-sm" data-testid="ops-buddy-open-admin">Open Buddy admin →</Link>
        </div>
      </div>
    </>
  );
}

function StaffBody({ data }) {
  const u = data.user;
  return (
    <div className="bg-white rounded-xl border p-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-slate-500 uppercase">Staff / Operator</div>
          <h1 className="text-2xl font-semibold text-slate-900" data-testid="ops-person-title">{u.name}</h1>
          <div className="text-sm text-slate-500">{u.email}</div>
        </div>
        <span className="text-lg font-semibold" data-testid="ops-person-status">{u.is_active === false ? "revoked" : "active"}</span>
      </div>
      <div className="mt-4 text-sm">Role: <span className="px-2 py-0.5 rounded bg-slate-100 text-xs">{u.role}</span></div>
      <p className="text-xs text-slate-500 mt-4">{data.federated_note}</p>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs text-slate-500 uppercase mb-1">{label}</span>
      {children}
    </label>
  );
}
